import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHome } from '../redux/hooks/hooks';
import { fetchHomeDataFromStrapi } from '../redux/reducers/home';
import { useDispatch } from 'react-redux';
import { BannerCarousel, LatestProducts, IndustrySolutions, GlobalPartnerSection, NewsSection, DirectorMessage } from '../components';
import AboutSection from '../components/AboutSection';
import GeographicPresenceSection from '../components/GeographicPresenceSection';
import ExcellenceCenterSection from '../components/ExcellenceCenterSection';
import CaseStudiesSection from '../components/CaseStudiesSection';
import ProductPortfolioSection from '../components/ProductPortfolioSection';
import CareersSection from '../components/CareersSection';
import ContactInfoSection from '../components/ContactInfoSection';
import { combineNewsAndBlogs } from '../utils/combineNewsAndBlogs';
import { useLanguage } from '../context';
import DataLoader from '../components/loader/DataLoader';
import SectionErrorBoundary from '../components/common/SectionErrorBoundary';

const HyteraPagePage = () => {
  const data = useHome();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const [combinedNews, setCombinedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHomeData = async () => {
      try {
        setLoading(true);
        await dispatch(fetchHomeDataFromStrapi(language));
      } catch (error) {
        console.error('Failed to load home data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadHomeData();
  }, [dispatch, language]);

  useEffect(() => {
    const loadCombinedNews = async () => {
      try {
        const combined = await combineNewsAndBlogs(language);
        setCombinedNews(combined);
      } catch (error) {
        console.error('Failed to load combined news and blogs:', error);
        // En cas d'erreur, on peut utiliser les données Redux comme solution de repli
        // mais pour l'instant, on laisse le tableau vide
      }
    };

    if (data?.sliders) {
      console.log(data.sliders);
    }

    loadCombinedNews();
  }, [language]);

  // Priority 1 (Tier 1): Essential Above-the-Fold content
  const isTier1Ready = 
    (data?.sliders && data.sliders.length > 0) && 
    (data?.aboutSection);

  // General check for ANY home data to avoid empty page if API fails partially
  // but we prioritize Tier 1 for the main loader
  const hasCriticalData = isTier1Ready || (data?.sliders && data.sliders.length > 0);

  // Afficher le loader tant que les données critiques ne sont pas chargées
  if (loading || !hasCriticalData) {
    return <DataLoader isLoading={loading || !hasCriticalData} />;
  }

  // Transform slider data to banner format (higher priority)
  const transformSlidersToBanners = (sliders) => {
    if (!sliders || !Array.isArray(sliders)) {
      return [];
    }

    return sliders.map((slider, index) => ({
      id: slider.id,
      title: slider.title,
      description: slider.description,
      image: slider.image,
      link: slider.link,
      alt: slider.alt || `Slider ${index + 1}`,
      color: slider.color,
      valeurInitiale: slider.valeurInitiale
    }));
  };

  // Transform product presentation data to banner format (fallback)
  const transformProductPresentationToBanners = (productPresentation) => {
    if (!productPresentation || !Array.isArray(productPresentation)) {
      return [];
    }

    return productPresentation.map((item, index) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      // The image URL is already processed in the reducer
      image: item.image?.formats?.large?.url || item.image?.url || '',
      link: item.link,
      alt: item.title || `Product ${index + 1}`
    }));
  };

  // Transform product presentation data to product format for LatestProducts
  const transformProductPresentationToProducts = (productPresentation) => {
    if (!productPresentation || !Array.isArray(productPresentation)) {
      return [];
    }

    return productPresentation.map((item, index) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      // The image URL is already processed in the reducer
      image: item.image?.formats?.large?.url || item.image?.url || '',
      link: item.link,
    }));
  };

  const banners = data?.sliders ? transformSlidersToBanners(data.sliders) : [];
  const products = transformProductPresentationToProducts(data?.productPresentation);
  const hasNews = combinedNews && combinedNews.length > 0;

  return (
    <div className="flex flex-col h-full">
      <Helmet>
        {data?.meta ? (
          <>
            <title>{data.meta}</title>
            <meta name="description" content={data.meta} />
            <meta property="og:title" content={data.meta} />
            <meta property="og:description" content={data.meta} />
          </>
        ) : null}
        <meta property="og:type" content="website" />
        {data?.meta ? (
          <>
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={data.meta} />
            <meta name="twitter:description" content={data.meta} />
          </>
        ) : null}
      </Helmet>
      <main className="flex-grow">
        <div className="relative">
          {banners.length > 0 ? (
            <BannerCarousel
              banners={banners}
              apiUrl={process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338'}
              scrollTargetId="hytera-content"
              scrollOffsetPx={105}
            />
          ) : null}
          <div id="hytera-content" className="relative bg-white">
            <div className="mt-0">
              <SectionErrorBoundary>
                <div>
                  {data?.aboutSection ? (
                    <AboutSection aboutData={data.aboutSection} />
                  ) : null}
                </div>
              </SectionErrorBoundary>

              <SectionErrorBoundary>
                <div>
                  {/* {data?.directorMessage && (
                    <DirectorMessage messageData={data.directorMessage} />
                  )} */}
                </div>
              </SectionErrorBoundary>

              <SectionErrorBoundary>
                <div>
                  {(data?.block2 && data.block2.length > 0) || (data?.block1 && data.block1.length > 0) ? (
                    <IndustrySolutions
                      block2={data?.block2 || []}
                      industries={data?.block1 || []}
                      apiUrl={process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338'}
                    />
                  ) : null}
                </div>
              </SectionErrorBoundary>

              <SectionErrorBoundary>
                <div>
                  {(data?.statsSection && data.statsSection.length > 0) || data?.productPortfolioSection ? (
                    <GlobalPartnerSection
                      stats={data?.statsSection}
                      block2={data?.block2}
                      revenueDistribution={data?.revenueDistribution}
                      title={data?.productPortfolioSection?.title}
                      description={data?.productPortfolioSection?.description}
                      apiUrl={process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338'}
                    />
                  ) : null}
                </div>
              </SectionErrorBoundary>

              <SectionErrorBoundary>
                <div>
                  {data?.geographicPresenceSection ? (
                    <GeographicPresenceSection geoData={data.geographicPresenceSection} />
                  ) : null}
                </div>
              </SectionErrorBoundary>

              <SectionErrorBoundary>
                <div>
                  {products.length > 0 ? (
                    <LatestProducts
                      products={products}
                      apiUrl={process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338'}
                    />
                  ) : null}
                </div>
              </SectionErrorBoundary>

              <SectionErrorBoundary>
                <div>
                  {hasNews ? (
                    <NewsSection
                      news={
                        combinedNews.length >= 3
                          ? {
                              bigEvent: combinedNews[0],
                              topNews: combinedNews[1],
                              bottomItems: combinedNews.slice(2, 5)
                            }
                          : combinedNews
                      }
                      blogs={[]}
                      apiUrl={process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338'}
                    />
                  ) : null}
                </div>
              </SectionErrorBoundary>

              <SectionErrorBoundary>
                <div>
                  {data?.excellenceCenterSection ? (
                    <ExcellenceCenterSection centerData={data.excellenceCenterSection} />
                  ) : null}
                </div>
              </SectionErrorBoundary>

              <SectionErrorBoundary>
                <div>
                  {data?.careersSection ? (
                    <CareersSection careersData={data.careersSection} />
                  ) : null}
                </div>
              </SectionErrorBoundary>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HyteraPagePage;
